const GA_ID = 'G-XH4W3CH3J6';
const CHOICE_KEY = 'cookiesAccepted';

// Kept in memory too, so a choice still holds for this visit if storage is blocked
let choiceThisVisit = null;
let loaded = false;

export const getCookieChoice = () => {
    if (choiceThisVisit !== null) return choiceThisVisit;
    try {
        return localStorage.getItem(CHOICE_KEY);
    } catch {
        return null;
    }
};

const hasConsent = () => getCookieChoice() === 'true';

// Guest order links carry a private code that must never reach Analytics
export const withoutPrivateKey = (href) => {
    const url = new URL(href);
    url.searchParams.delete('key');
    return url.href;
};

export const loadAnalytics = () => {
    if (loaded || !hasConsent()) return;
    loaded = true;

    window.dataLayer = window.dataLayer || [];
    window.gtag = function gtag() {
        window.dataLayer.push(arguments); // eslint-disable-line prefer-rest-params
    };
    window.gtag('js', new Date());
    window.gtag('config', GA_ID, { page_location: withoutPrivateKey(window.location.href) });

    const script = document.createElement('script');
    script.async = true;
    script.src = `https://www.googletagmanager.com/gtag/js?id=${GA_ID}`;
    document.head.appendChild(script);
};

export const setCookieChoice = (accepted) => {
    choiceThisVisit = accepted ? 'true' : 'false';
    try {
        localStorage.setItem(CHOICE_KEY, choiceThisVisit);
    } catch {
        // Storage blocked: the in-memory choice covers this visit
    }
    if (accepted) loadAnalytics();
};

const trackEvent = (name, params) => {
    if (!hasConsent()) return false;
    loadAnalytics();
    window.gtag('event', name, params);
    return true;
};

// The price actually charged: only clearance items get their discount
const salePrice = ({ price, isClearance, discount }) => {
    const full = Number(price) || 0;
    return isClearance && discount > 0 ? Number((full - (full * discount) / 100).toFixed(2)) : full;
};

const toGaItem = (product, qty) => ({
    item_id: String(product._id),
    item_name: product.name,
    item_category: product.productType,
    price: salePrice(product),
    quantity: qty,
});

const shopEvent = (name, items) => trackEvent(name, {
    currency: 'GBP',
    value: Number(items.reduce((total, item) => total + item.price * item.quantity, 0).toFixed(2)),
    items,
});

export const trackViewItem = (product) => shopEvent('view_item', [toGaItem(product, 1)]);

export const trackAddToCart = (product, qty) => shopEvent('add_to_cart', [toGaItem(product, qty)]);

export const trackBeginCheckout = (cartItems) =>
    shopEvent('begin_checkout', cartItems.map((item) => toGaItem(item, item.qty)));

// Sends a purchase once only, so refreshing the confirmation page doesn't count it twice
export const trackPurchase = (order) => {
    const orderId = String(order._id);
    const key = `gaPurchase:${orderId}`;
    try {
        if (localStorage.getItem(key)) return;
    } catch {
        // Storage blocked: still report the sale
    }

    const sent = trackEvent('purchase', {
        transaction_id: String(order.orderNumber || orderId),
        currency: 'GBP',
        value: Number(order.totalPrice) || 0,
        shipping: Number(order.shippingPrice) || 0,
        coupon: order.promoCode || undefined,
        items: (order.orderItems || []).map((item) => ({
            ...toGaItem({ ...item, _id: item.product || item._id }, item.qty),
        })),
    });

    if (sent) {
        try {
            localStorage.setItem(key, '1');
        } catch {
            // Nothing else to do
        }
    }
};
